/**
 * Right-pane router for the Voluum Standard paths workspace:
 *  - default-path -> PathDetailColumn bound to draft.defaultPaths
 *  - group-conditions -> ConditionsEditor bound to a rule group
 *  - group-path -> PathDetailColumn bound to a rule group's path
 */

import type { FC } from 'react';
import type {
  VoluumFlowUpdate,
  VoluumPathGroupUpdate,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

import { ConditionsEditor } from '../../groups/conditions';
import { getSetAutoOptimized } from '../../utils/set-automation';
import { useTrackerAi } from '@/features/flow-optimizer/tracker';

import PathDetailColumn from './PathDetailColumn';
import {
  defaultPathSelection,
  type PathsSelection,
} from './selection';

export interface PathsRightPaneProps {
  selection: PathsSelection;
  draft: VoluumFlowUpdate;
  paths: VoluumPathUpdate[];
  groups: VoluumPathGroupUpdate[];
  patchDefaultPath: (idx: number, m: (p: VoluumPathUpdate) => void) => void;
  patchGroup: (idx: number, m: (g: VoluumPathGroupUpdate) => void) => void;
  patchGroupPath: (
    groupIdx: number,
    pathIdx: number,
    m: (p: VoluumPathUpdate) => void,
  ) => void;
  onSelectionChange: (s: PathsSelection) => void;
}

const PathsRightPane: FC<PathsRightPaneProps> = ({
  selection,
  draft,
  paths,
  groups,
  patchDefaultPath,
  patchGroup,
  patchGroupPath,
  onSelectionChange,
}) => {
  // A tracker without its own AI ignores the auto-optimized flags.
  const trackerAi = useTrackerAi();
  if (selection.kind === 'default-path') {
    const path = paths[selection.index];
    if (paths.length === 0 || !path) {
      return (
        <p className="text-xs text-muted-foreground">
          No default paths on this flow.
        </p>
      );
    }
    return (
      <PathDetailColumn
        key={path.id ?? selection.index}
        path={path}
        weightLocked={trackerAi && getSetAutoOptimized(draft)}
        onPatch={(m) => patchDefaultPath(selection.index, m)}
      />
    );
  }
  const group = groups[selection.groupIndex];
  if (!group) return null;
  if (selection.kind === 'group-conditions') {
    return (
      <ConditionsEditor
        key={group.id ?? `group-${selection.groupIndex}`}
        group={group}
        onApply={(next) =>
          patchGroup(selection.groupIndex, (g) => {
            g.conditions = next;
          })
        }
        onClose={() => onSelectionChange(defaultPathSelection(0))}
      />
    );
  }
  const path = group.paths?.[selection.pathIndex];
  if (!path) return null;
  return (
    <PathDetailColumn
      key={path.id ?? `${selection.groupIndex}-${selection.pathIndex}`}
      path={path}
      weightLocked={trackerAi && getSetAutoOptimized(group)}
      onPatch={(m) =>
        patchGroupPath(selection.groupIndex, selection.pathIndex, m)
      }
    />
  );
};

export default PathsRightPane;
